import {
    CaretDown,
    Check,
    Gear,
    Plus,
    UserPlus
} from '@phosphor-icons/react';
import clsx from 'clsx';
import {
    useEffect, useRef, useState
} from 'react';
import { createPortal } from 'react-dom';

import { useAdminContext } from '@/contexts/AdminContext';
import { useAuth } from '@/contexts/AuthContext';
import { Permission } from '@/contexts/AuthContext/permissions';
import { Workspace } from '@/contexts/AuthContext/useWorkspaces';
import { useOnboarding } from '@/contexts/OnboardingContext';

interface WorkspaceItemProps {
    workspace: Workspace;
    isActive: boolean;
    onClick?: () => void;
}

// 辅助函数：从 workspace subscription 计算订阅状态
const getWorkspaceSubscriptionStatus = (subscription: Workspace['subscription']) => {
    if (!subscription) {
        return {
            planDisplayText: 'Basic',
            isExpired: false,
        };
    }

    const now = new Date().getTime();
    const currentPeriodEnd = subscription.current_period_end
        ? new Date(subscription.current_period_end).getTime()
        : null;

    // 检查是否过期
    const isExpired = currentPeriodEnd && now > currentPeriodEnd;

    if (isExpired) {
        return {
            planDisplayText: 'Expired',
            isExpired: true,
        };
    }

    return {
        planDisplayText: subscription.plan_name || 'Basic',
        isExpired: false,
    };
};

const WorkspaceAvatar = ({
    workspace, className, fontSize = "text-sm" 
}: { workspace: Workspace, className?: string, fontSize?: string }) => {
    const [imageError, setImageError] = useState(false);
    
    // If logo_url exists and hasn't failed to load
    if (workspace.logo_url && !imageError) {
        return (
            <img
                src={workspace.logo_url}
                alt={workspace.name}
                className={clsx("w-full h-full object-contain p-0.5", className)}
                onError={() => setImageError(true)}
            />
        );
    }
    
    // Fallback to initial
    return (
        <div className={clsx("flex items-center justify-center w-full h-full font-bold text-gray-800 bg-gray-200", fontSize, className)}>
            {workspace.name?.charAt(0).toUpperCase() || 'W'}
        </div>
    );
};

const WorkspaceItem = ({
    workspace, isActive, onClick
}: WorkspaceItemProps) => {
    const { planDisplayText, isExpired } = getWorkspaceSubscriptionStatus(workspace.subscription);

    return (
        <div
            onClick={onClick}
            className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer group"
        >
            <div className="flex items-center gap-3 overflow-hidden min-w-0 flex-1">
                {/* Logo */}
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-100 text-gray-700 shrink-0 overflow-hidden">
                    <WorkspaceAvatar workspace={workspace} fontSize="text-sm" />
                </div>

                {/* Name */}
                <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{workspace.name}</div>
                </div>

                {/* Plan Tag */}
                {isExpired ? (
                    <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-bold rounded uppercase shrink-0">
                        EXPIRED
                    </span>
                ) : (
                    <span className="px-1.5 py-0.5 bg-[#1A162F] text-white text-[10px] font-bold rounded uppercase shrink-0">
                        {planDisplayText}
                    </span>
                )}
            </div>

            {/* Check Icon */}
            {isActive ? <Check size={16} weight="bold" className="text-gray-900 shrink-0 ml-2" /> : null}
        </div>
    );
};

WorkspaceItem.displayName = 'WorkspaceItem';

interface WorkspaceSwitcherProps {
    collapsed: boolean;
}

const WorkspaceSwitcher = ({ collapsed }: WorkspaceSwitcherProps) => {
    const {
        workspaces, currentWorkspace, switchWorkspace, userBilling, permissions, workspaceMembers
    } = useAuth();
    const { modal } = useAdminContext();
    const { startOnboarding } = useOnboarding();
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // 检查是否有订阅管理权限
    const canManageSubscription = permissions.checkPermissions([Permission.SUBSCRIPTION_MANAGE]);

    // Ensure we have a valid workspace object to display
    const activeWorkspace = currentWorkspace || workspaces[0] || {
        id: 'dummy',
        name: 'Select Workspace',
        owner_id: '',
        status: 'ACTIVE',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
    } as Workspace;

    // Get plan information from userBilling (for Upgrade Banner)
    const { planText } = userBilling;
    const membersCount = workspaceMembers.members.length;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const openSettings = (tab: string) => {
        modal.openModal('profile', { activeTab: tab });
        setIsOpen(false);
    };

    if (!workspaces || workspaces.length === 0) return null;

    return (
        <div className={clsx("relative", collapsed && "w-fit mx-auto")} ref={menuRef}>
            {collapsed ? (
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={clsx(
                        "w-10 h-10 flex items-center justify-center rounded-xl transition-colors border",
                        isOpen
                            ? "bg-white border-primary ring-1 ring-primary shadow-md"
                            : "bg-transparent border-transparent hover:border-gray-200 hover:bg-white text-gray-700"
                    )}
                >
                    <div className="w-6 h-6 flex items-center justify-center rounded overflow-hidden">
                        <WorkspaceAvatar workspace={activeWorkspace} className="w-full h-full object-contain" />
                    </div>
                </button>
            ) : (
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={clsx(
                        "flex items-center gap-3 px-4 py-2 rounded-xl transition-all w-full text-left border group relative z-10",
                        isOpen
                            ? "bg-white border-primary ring-1 ring-primary shadow-md"
                            : "bg-white border-gray-200 shadow-sm hover:border-gray-300"
                    )}
                >
                    <div className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 text-gray-700 shrink-0 border border-gray-100 group-hover:border-gray-200 transition-colors overflow-hidden">
                        <WorkspaceAvatar workspace={activeWorkspace} className="w-full h-full object-contain p-0.5" fontSize="text-xs" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">{activeWorkspace.name}</div>
                    </div>
                    <CaretDown size={12} weight="bold" className="text-gray-400 group-hover:text-gray-600 transition-colors" />
                </button>
            )}

            {isOpen ? createPortal(
                <div
                    className="fixed w-[340px] mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 z-[9999] animate-in fade-in zoom-in-95 duration-100 overflow-hidden"
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                        top: menuRef.current?.getBoundingClientRect().bottom! + 4,
                        left: menuRef.current?.getBoundingClientRect().left
                    }}
                >
                    {/* Header: Current Workspace Info */}
                    <div className="p-4 pb-2">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-700 shrink-0 overflow-hidden shadow-sm">
                                <WorkspaceAvatar workspace={activeWorkspace} className="w-full h-full object-contain p-1" fontSize="text-base" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-base font-bold text-gray-900 truncate leading-tight mb-0.5">{activeWorkspace.name}</div>
                                <div className="text-xs text-gray-500 flex items-center gap-1">
                                    {membersCount}
                                    {' '}
                                    member
                                    {membersCount !== 1 && 's'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="px-4 pb-4 flex items-center gap-2">
                        <button 
                            onClick={() => openSettings('workspace-settings')}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
                        >
                            <Gear size={16} weight="bold" />
                            {' '}
                            Settings
                        </button>
                        <button 
                            onClick={() => openSettings('members')}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
                        >
                            <UserPlus size={16} weight="bold" />
                            {' '}
                            Invite members
                        </button>
                    </div>

                    {/* Upgrade Banner */}
                    <div className="mx-4 mb-4 p-3 bg-gray-50 rounded-xl flex items-center justify-between border border-gray-100">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-900">
                                {planText.planDisplayText}
                            </span>
                            {planText.badge ? (
                                <span className="px-2 py-0.5 rounded-full bg-gray-200 text-[10px] font-bold text-gray-600">
                                    {planText.badge}
                                </span>
                            ) : null}
                        </div>
                        {canManageSubscription ? (
                            <button
                                onClick={() => {
                                    modal.openModal('pricing');
                                    setIsOpen(false);
                                }}
                                className={clsx(
                                    "px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors shadow-sm",
                                    planText.buttonText === 'Upgrade' || planText.buttonText === 'Renew'
                                        ? "bg-[#1A162F] text-white hover:bg-[#1A162F]/90"
                                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                                )}
                            >
                                {planText.buttonText}
                            </button>
                        ) : null}
                    </div>

                    {/* Divider */}
                    <div className="h-px bg-gray-100 mx-0 mb-2" />

                    {/* All Workspaces Header */}
                    <div className="px-4 py-2 text-xs font-medium text-gray-500">
                        All workspaces
                    </div>

                    {/* Workspace List */}
                    <div className="px-2 pb-2 space-y-0.5 max-h-[200px] overflow-y-auto custom-scrollbar">
                        {workspaces.map(ws => (
                            <WorkspaceItem
                                key={ws.id}
                                workspace={ws}
                                isActive={ws.id === currentWorkspace?.id}
                                onClick={() => {
                                    switchWorkspace(ws.id);
                                    setIsOpen(false);
                                }}
                            />
                        ))}
                    </div>

                    {/* Footer: Create Workspace */}
                    <div className="p-2 border-t border-gray-100 mt-1">
                        <button
                            onClick={() => {
                                startOnboarding();
                                setIsOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors text-gray-600 hover:text-gray-900 group"
                        >
                            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 border border-gray-200 border-dashed shrink-0 text-gray-400 group-hover:text-gray-600 group-hover:border-gray-400 transition-colors">
                                <Plus size={16} weight="bold" />
                            </div>
                            <span className="text-sm font-medium">Create new workspace</span>
                        </button>
                    </div>
                </div>,
                document.body
            ) : null}
        </div>
    );
};

export default WorkspaceSwitcher;
